import { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";

export const HERO_THEMES = {
  grid: { poster: "/hero/grid.jpg", video: "https://videos.pexels.com/video-files/5422905/5422905-hd_1366_720_24fps.mp4", label: "Transmission & grid" },
  storage: { poster: "/hero/storage.jpg", label: "Battery storage", kenBurns: "strong" },
  solar: { poster: "/hero/solar.jpg", video: "https://assets.mixkit.co/videos/32448/32448-720.mp4", label: "Solar" },
  wind: { poster: "/hero/wind.jpg", video: "https://assets.mixkit.co/videos/8275/8275-720.mp4", label: "Wind" },
  hydrogen: { poster: "/hero/hydrogen.jpg", label: "Green hydrogen" },
  finance: { poster: "/hero/finance.jpg", video: "https://videos.pexels.com/video-files/14003933/14003933-hd_1920_1080_60fps.mp4", label: "Tariffs & financing" },
  datacentre: { poster: "/hero/datacentre.jpg", video: "https://videos.pexels.com/video-files/5028622/5028622-hd_1920_1080_25fps.mp4", label: "Data-centre power" },
  rtc: { poster: "/hero/rtc.jpg", video: "https://videos.pexels.com/video-files/5396795/5396795-hd_1920_1080_30fps.mp4", label: "Round-the-clock power" },
  energy: { poster: "/hero-poster.jpg", video: "https://videos.pexels.com/video-files/2098989/2098989-hd_1920_1080_30fps.mp4", label: "Energy transition" },
};

const RULES = [
  ["grid", /\b(grid|grids|transmission|evacuat|connectivit|bottleneck|substation|pylon|line|ists|gna)\b/i],
  ["storage", /\b(storage|batter|bess|cell|cells|lfp|lithium|gwh)\b/i],
  ["hydrogen", /\b(hydrogen|h2|electrolys|ammonia)\b/i],
  ["datacentre", /\b(data[- ]?cent|ai load|compute|hyperscal)\b/i],
  ["rtc", /\b(round[- ]the[- ]clock|rtc|fdre|firm|hybrid|24x7|dispatchable)\b/i],
  ["finance", /\b(tariff|tariffs|auction|bid|bids|financ|debt|capital|ppa|bankab|rupee|₹|price|prices|cost)\b/i],
  ["solar", /\b(solar|pv|module|modules|kusum|rooftop)\b/i],
  ["wind", /\b(wind|turbine|offshore)\b/i],
];

export const classifyHeroTheme = (fresh) => {
  if (fresh?.theme && HERO_THEMES[fresh.theme]) return fresh.theme;
  const text = [fresh?.headline_a, fresh?.headline_b, fresh?.accent, fresh?.sub].filter(Boolean).join(" ");
  for (const [k, rx] of RULES) if (rx.test(text)) return k;
  return "energy";
};

export const HeroBackdrop = ({ fresh }) => {
  const theme = useMemo(() => classifyHeroTheme(fresh), [fresh]);
  const [videoOk, setVideoOk] = useState(true);
  const [mobile, setMobile] = useState(() => window.matchMedia("(max-width: 767px)").matches);
  useEffect(() => { setVideoOk(true); }, [theme]);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const fn = (e) => setMobile(e.matches);
    mq.addEventListener("change", fn);
    return () => mq.removeEventListener("change", fn);
  }, []);
  const t = HERO_THEMES[theme];
  const useVideo = Boolean(t.video) && videoOk && !mobile;
  return (
    <AnimatePresence mode="sync">
      <motion.div key={theme} className="absolute inset-0" data-testid="hero-backdrop" data-theme={theme}
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 1.4, ease: "easeInOut" }}>
        <motion.img src={t.poster} alt={`${t.label || "Clean energy"} — Growthifye`} className="absolute inset-0 w-full h-full object-cover opacity-60" data-testid="hero-poster"
          initial={{ scale: 1, x: 0 }} animate={{ scale: useVideo ? 1 : t.kenBurns === "strong" ? 1.16 : 1.08, x: useVideo ? 0 : t.kenBurns === "strong" ? -40 : 0 }}
          transition={{ duration: t.kenBurns === "strong" ? 22 : 28, ease: "linear", repeat: Infinity, repeatType: "reverse" }} />
        {useVideo && (
          <video data-testid="hero-video" className="absolute inset-0 w-full h-full object-cover opacity-60" src={t.video} poster={t.poster}
            autoPlay muted loop playsInline preload="auto" onError={() => setVideoOk(false)} />
        )}
        <span className="sr-only" data-testid="hero-theme-label">{t.label}</span>
      </motion.div>
    </AnimatePresence>
  );
};
